/**
 * @license
 * @preserve
 *
 * KeeeX SAS Confidential
 * https://keeex.me
 * Copyright 2013-2022 KeeeX All Rights Reserved.
 * 
 * These computer program listings and specifications, herein,
 * are and remain the property of KeeeX SAS. The intellectual
 * and technical concepts herein are proprietary to KeeeX SAS
 * and may be covered by EU and foreign patents,
 * patents in process, trade secrets and copyright law.
 * 
 * They shall not be reproduced or copied or used in whole or
 * in part as the basis for manufacture or sale of items unless
 * prior written permission is obtained from KeeeX SAS.
 * 
 * For a license agreement, please contact:
 * <mailto: contact@keeex.net>
 * 
 */
import { getUint8Array } from "@keeex/js-utils/lib/uint8array.js";
import { digest, digestBlockSize, digestImmediate } from "../digest.js";
/**
 * Fallback implementation of a HMAC that support chunked input.
 *
 * Handle computation using digest module.
 */
export default class ChunkHmac {
    algorithm;
    key;
    outerKeyPad;
    innerDigest;
    constructor(algorithm, key) {
        this.algorithm = algorithm;
        this.key = getUint8Array(key, undefined, undefined, true);
    }
    /**
     * Return an appropriate length key for HMAC
     */
    static async padKey(algorithm, key) {
        const blockSize = digestBlockSize(algorithm);
        if (key.byteLength > blockSize) {
            return digestImmediate(algorithm, key);
        }
        if (key.byteLength < blockSize) {
            const paddedKey = new Uint8Array(blockSize);
            paddedKey.set(key);
            paddedKey.fill(0, key.byteLength);
            return paddedKey;
        }
        return key;
    }
    /**
     * XOR a byte buffer with the given byte
     */
    static xorBuffer(input, byteKey) {
        const output = new Uint8Array(input.byteLength);
        for (let i = 0; i < input.byteLength; ++i) {
            output[i] = input[i] ^ byteKey;
        }
        return output;
    }
    async update(data) {
        if (!this.innerDigest) {
            const initRes = await this.computeKeyPads();
            this.outerKeyPad = initRes.outerKeyPad;
            this.innerDigest = initRes.innerDigest;
        }
        await this.innerDigest.update(data);
    }
    async hmac(data) {
        if (data) {
            await this.update(data);
        }
        if (!this.outerKeyPad || !this.innerDigest) {
            const initRes = await this.computeKeyPads();
            this.outerKeyPad = initRes.outerKeyPad;
            this.innerDigest = initRes.innerDigest;
        }
        const outerDigest = await digest(this.algorithm);
        await outerDigest.update(this.outerKeyPad);
        return outerDigest.digest(await this.innerDigest.digest());
    }
    async computeKeyPads() {
        if (!this.key) {
            throw new Error("Unexpected state");
        }
        const paddedKey = await ChunkHmac.padKey(this.algorithm, this.key);
        this.key = undefined;
        const OUTER_PAD_VALUE = 0x5c;
        const INNER_PAD_VALUE = 0x36;
        const outerKeyPad = ChunkHmac.xorBuffer(paddedKey, OUTER_PAD_VALUE);
        const innerKeyPad = ChunkHmac.xorBuffer(paddedKey, INNER_PAD_VALUE);
        const innerDigest = await digest(this.algorithm);
        await innerDigest.update(innerKeyPad);
        return {
            outerKeyPad,
            innerDigest,
        };
    }
}
